import { useState, useEffect } from 'react'
import {
  Building2,
  Users,
  AlertTriangle,
  Database,
  Key,
  RefreshCw,
  TrendingUp,
  Activity
} from 'lucide-react'

interface DashboardMetrics {
  tenants: {
    total: number
    active: number
    inactive: number
  }
  sessions: {
    active: number
    providers: {
      google: number
      github: number
    }
  }
  audit: {
    totalLogs: number
    byAction: Record<string, number>
    byActor: Record<string, number>
    bySeverity: Record<string, number>
    byOutcome: Record<string, number>
  }
  storage: {
    totalBlobs: number
    stores: number
  }
  platformKeys: {
    authorizedTenants: number
    enabled: number
    disabled: number
  }
}

interface Alert {
  id: string
  severity: string
  action: string
  actor: string
  timestamp: string
  details: string
}

interface PlatformAdminDashboardProps {
  onNavigate?: (tab: string) => void
}

const PlatformAdminDashboard = ({ onNavigate }: PlatformAdminDashboardProps) => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)

  useEffect(() => {
    loadDashboard()
  }, [])

  const loadDashboard = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/platform-admin/dashboard', {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to load dashboard')
      }

      const data = await response.json()
      setMetrics(data.metrics)
      setAlerts(data.alerts || [])
      setLastUpdated(data.lastUpdated)
    } catch (err) {
      console.error('Dashboard error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200'
    }
  }

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading dashboard...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <div>
            <h3 className="font-semibold text-red-900">Error Loading Dashboard</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={loadDashboard}
          className="mt-4 btn-primary text-sm"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!metrics) {
    return null
  }

  const metricCards = [
    {
      name: 'Total Tenants',
      value: metrics.tenants.total.toString(),
      subtitle: `${metrics.tenants.active} active, ${metrics.tenants.inactive} inactive`,
      icon: Building2,
      color: 'bg-blue-100 text-blue-600',
      tab: 'tenants'
    },
    {
      name: 'Active Sessions',
      value: metrics.sessions.active.toString(),
      subtitle: `${metrics.sessions.providers.google} Google, ${metrics.sessions.providers.github} GitHub`,
      icon: Users,
      color: 'bg-green-100 text-green-600',
      tab: 'analytics'
    },
    {
      name: 'Authorized Tenants',
      value: metrics.platformKeys.authorizedTenants.toString(),
      subtitle: `${metrics.platformKeys.enabled} enabled`,
      icon: Key,
      color: 'bg-purple-100 text-purple-600',
      tab: 'keys'
    },
    {
      name: 'Audit Logs (7d)',
      value: metrics.audit.totalLogs.toString(),
      subtitle: `${metrics.audit.bySeverity.critical || 0} critical, ${metrics.audit.bySeverity.warning || 0} warnings`,
      icon: Activity,
      color: 'bg-orange-100 text-orange-600',
      tab: 'audit'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-sm text-gray-600 mt-1">
            Platform overview and recent activity
          </p>
        </div>
        <button
          onClick={loadDashboard}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <p className="text-xs text-gray-500">
          Last updated: {new Date(lastUpdated).toLocaleString()}
        </p>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((metric, index) => {
          const IconComponent = metric.icon
          return (
            <div
              key={index}
              onClick={() => onNavigate?.(metric.tab)}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md hover:border-blue-300 transition cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{metric.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{metric.subtitle}</p>
                </div>
                <div className={`p-3 rounded-lg ${metric.color}`}>
                  <IconComponent className="w-6 h-6" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Storage Stats */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Database className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Storage</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Blobs</span>
              <span className="text-sm font-semibold text-gray-900">
                {metrics.storage.totalBlobs}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Blob Stores</span>
              <span className="text-sm font-semibold text-gray-900">
                {metrics.storage.stores}
              </span>
            </div>
          </div>
        </div>

        {/* Audit Summary */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Activity (7 days)</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Success Rate</span>
              <span className="text-sm font-semibold text-green-600">
                {metrics.audit.totalLogs > 0
                  ? Math.round(
                      ((metrics.audit.byOutcome.success || 0) / metrics.audit.totalLogs) * 100
                    )
                  : 0}
                %
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Failed Actions</span>
              <span className="text-sm font-semibold text-red-600">
                {metrics.audit.byOutcome.failure || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Alerts */}
      {alerts.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <h3 className="text-lg font-semibold text-gray-900">Recent Alerts (24h)</h3>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {alerts.slice(0, 5).map((alert) => (
              <div key={alert.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getSeverityColor(
                          alert.severity
                        )}`}
                      >
                        {alert.severity}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{alert.action}</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      <strong>Actor:</strong> {alert.actor}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {alerts.length > 5 && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                View all {alerts.length} alerts →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:shadow-md transition">
            <Building2 className="w-5 h-5 text-blue-600 mb-2" />
            <p className="font-medium text-gray-900">Create Tenant</p>
            <p className="text-xs text-gray-600 mt-1">Add a new trust center tenant</p>
          </button>
          <button className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:shadow-md transition">
            <Key className="w-5 h-5 text-purple-600 mb-2" />
            <p className="font-medium text-gray-900">Manage Platform Keys</p>
            <p className="text-xs text-gray-600 mt-1">Configure shared API keys</p>
          </button>
          <button className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:shadow-md transition">
            <Activity className="w-5 h-5 text-orange-600 mb-2" />
            <p className="font-medium text-gray-900">View Audit Logs</p>
            <p className="text-xs text-gray-600 mt-1">Review security events</p>
          </button>
        </div>
      </div>
    </div>
  )
}

export default PlatformAdminDashboard
